import { useLocation } from "react-router-dom";
import { useThemeBaseRoot } from "./useThemeBase";

/**
 * useTenantHreflang — Computes the EN/ES hreflang alternates AND the current
 * page language for any tenant microsite URL (including `/s/{subdomain}` preview
 * URLs, real tenant subdomains, and `/preview-{theme}` demo routes).
 *
 * Usage in a page/layout that renders <SEOHead>:
 *   const { lang, alternates } = useTenantHreflang();
 *   <SEOHead title="…" description="…" lang={lang} alternates={alternates} />
 *
 * Returns:
 *   {
 *     lang: "en" | "es",
 *     alternates: {
 *       en: "https://foo.com/about",
 *       es: "https://foo.com/es/about",
 *     }
 *   }
 *
 * How it works:
 *   1. `useThemeBaseRoot()` yields the language-free base path ("", "/s/sub",
 *      or "/preview-bounce").
 *   2. Strip the base to get the per-page suffix.
 *   3. If the suffix begins with "/es", we're on the Spanish page → swap to
 *      English by removing "/es". Otherwise we're on English → build the
 *      Spanish twin by injecting "/es" after the base.
 *   4. Compose absolute URLs using window.location.origin.
 */
export function useTenantHreflang() {
  const { pathname, search } = useLocation();
  const base = useThemeBaseRoot();

  const afterBase = pathname.slice(base.length);
  const isEs = afterBase === "/es" || afterBase.startsWith("/es/");

  let enPath;
  let esPath;
  if (isEs) {
    const rest = afterBase.slice(3) || "/";
    enPath = `${base}${rest}`;
    esPath = pathname;
  } else {
    enPath = pathname;
    const rest = afterBase === "/" || afterBase === "" ? "" : afterBase;
    esPath = `${base}/es${rest}`;
  }

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  return {
    lang: isEs ? "es" : "en",
    alternates: {
      en: `${origin}${enPath}${search || ""}`,
      es: `${origin}${esPath}${search || ""}`,
    },
  };
}
